/*
 * School modal · Counsellors table: who worked this school in the period.
 *
 * Ports LAE `features/productivity/SchoolModalTables.jsx` `SchoolCounsellorsBlock` (build spec
 * §7.5). Columns, leader maxima, the Team median footer and the CoverageBar all come from
 * `lae-counsellor-columns.tsx`; this file is the table around them: filters, ColumnManager,
 * frozen header and name, rank numbering, local sort, the phone card list.
 *
 * Deliberate differences from LAE:
 * - Rows and names are NOT clickable: there is no counsellor modal (owner 2026-10-29).
 * - No rubric (no Productivity Score column, no rubric loading state), no drill-downs, no
 *   definition tooltips (§0.3, §0.4).
 * - Filters are `useTableFilters` over the whole row set (every column page-scoped, which is the
 *   whole dataset here). Numeric columns pass `kind: 'number'`, so their popovers also offer
 *   number conditions; durations filter in minutes.
 * - The rank column (`#`) is not a layout column: it is not listed in the ColumnManager, cannot be
 *   hidden, moved or filtered, and is frozen left ahead of the code-pinned Counsellor column. It
 *   numbers rows under the CURRENT sort and filters.
 * - Sort is the shared header sort (remembered with the filters); none chosen = Total Calls
 *   Dialled, highest first. Nulls sort last in either direction.
 * - A metric the endpoint never sends (`undefined` on every row) has no column at all.
 * - Below `md` the table becomes a card list with the same visible columns, plus a Team median
 *   card.
 */
import { useMemo, useRef, type ReactNode } from 'react';
import {
  Badge,
  Button,
  ColumnManager,
  EmptyState,
  FilterSheet,
  FilterableTH,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type FilterableTHProps,
  type FrozenSide,
} from '@/components/ui';
import type { LaeProdCounsellorRow } from '@/lib/types';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { PiCardField, PiCardList } from '../pi-kit';
import { fmtNum } from './lae-format';
import {
  counsellorFooterCells,
  counsellorLeaders,
  createCounsellorColumns,
  type CounsellorColumn,
  type CounsellorRow,
} from './lae-counsellor-columns';

export interface SchoolCounsellorsTableProps {
  rows: readonly LaeProdCounsellorRow[] | null | undefined;
  /** The modal's funnel value; the admit funnel swaps Applications for Reached Deposit / Visa. */
  funnel: string | null | undefined;
}

const ADMIT_FUNNEL = 'admit_to_enrollment';
const SCOPE = 'product-insights.lae.school-modal.counsellors';
const MEMORY_ID = 'lae-modal-counsellors';
const RANK_KEY = 'rank';
const NAME_KEY = 'name';
const DEFAULT_SORT = { key: 'calls', direction: 'desc' } as const;
const LOCKED_KEYS: readonly string[] = [NAME_KEY];
const NO_ROWS: readonly LaeProdCounsellorRow[] = [];

const isBlank = (v: unknown): boolean => v === null || v === undefined || v === '';

/** Nulls last whatever the direction; text compares by locale, case-insensitive. */
function sortRows(
  rows: readonly CounsellorRow[],
  column: CounsellorColumn | undefined,
  direction: 'asc' | 'desc',
): CounsellorRow[] {
  if (!column) return [...rows];
  const mul = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = column.sortValue(a);
    const vb = column.sortValue(b);
    const aNull = isBlank(va);
    const bNull = isBlank(vb);
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
    return String(va).localeCompare(String(vb), 'en-GB', { sensitivity: 'base' }) * mul;
  });
}

/** Filter values: figures read as figures (durations are already minutes, 1 dp). */
function formatFigure(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? fmtNum(n) : value;
}

function toSpec(column: CounsellorColumn): ColumnSpec<CounsellorRow> {
  return {
    key: column.key,
    label: column.label,
    get: column.get,
    sortKey: column.key,
    numeric: column.numeric,
    ...(column.kind === 'number' ? { kind: 'number' as const, format: formatFigure } : {}),
  };
}

function countText(count: number): string {
  return count === 1 ? '1 counsellor' : `${count.toLocaleString('en-GB')} counsellors`;
}

export function SchoolCounsellorsTable({ rows: given, funnel }: SchoolCounsellorsTableProps) {
  const rows = (Array.isArray(given) ? given : NO_ROWS) as readonly CounsellorRow[];
  const admit = funnel === ADMIT_FUNNEL;

  const presentKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of rows) {
      for (const [key, value] of Object.entries(row)) if (value !== undefined) keys.add(key);
    }
    return keys;
  }, [rows]);
  const presentSignature = [...presentKeys].sort().join('|');

  const max = useMemo(() => counsellorLeaders(rows), [rows]);
  const allColumns = useMemo(
    () => createCounsellorColumns({ admit, max, presentKeys }),
    // `presentKeys` is keyed by its signature so a refetch with the same shape keeps the columns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [admit, max, presentSignature],
  );
  const rankColumn = allColumns.find((column) => column.key === RANK_KEY);
  const columns = useMemo(() => allColumns.filter((column) => column.key !== RANK_KEY), [allColumns]);
  const columnByKey = useMemo(
    () => new Map(columns.map((column): [string, CounsellorColumn] => [column.key, column])),
    [columns],
  );
  const columnKeys = useMemo(() => columns.map((column) => column.key), [columns]);

  const specs = useMemo(() => columns.map(toSpec), [columns]);
  const filters = useTableFilters(rows as CounsellorRow[], specs, { memoryId: MEMORY_ID });
  const { visibleRows, headerFor, activeCount, clearAll } = filters;

  const sort = filters.sort && columnByKey.has(filters.sort.key) ? filters.sort : DEFAULT_SORT;
  const ranked = useMemo(
    () =>
      sortRows(visibleRows, columnByKey.get(sort.key), sort.direction).map(
        (row, index): CounsellorRow => ({ ...row, __rank: index + 1 }),
      ),
    [visibleRows, columnByKey, sort.key, sort.direction],
  );

  const layout = useColumnLayout(SCOPE, columnKeys, LOCKED_KEYS, [], { pinnedLeftKeys: LOCKED_KEYS });
  const { visibleKeys, frozenSideOf } = layout;

  // The rank cell leads the header row, so it leads the measured keys too.
  const measuredKeys = useMemo(() => [RANK_KEY, ...visibleKeys], [visibleKeys]);
  const measuredSideOf = useMemo(
    () =>
      (key: string): FrozenSide | undefined =>
        key === RANK_KEY ? 'left' : frozenSideOf(key),
    [frozenSideOf],
  );
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const offsets = useFrozenOffsets(headRowRef, measuredKeys, measuredSideOf);
  const frozenProps = (key: string): { frozen?: FrozenSide; frozenOffset?: number } => {
    const side = measuredSideOf(key);
    return side === undefined ? {} : { frozen: side, frozenOffset: offsets[key] ?? 0 };
  };

  const footer = useMemo(() => counsellorFooterCells(rows, { admit }), [rows, admit]);

  const headers = new Map<string, FilterableTHProps>(
    columnKeys.map((key): [string, FilterableTHProps] => [key, headerFor(key)]),
  );
  const labelOf = (key: string) => columnByKey.get(key)?.label ?? key;
  const metricKeys = visibleKeys.filter((key) => key !== NAME_KEY);

  const filteredEmpty = visibleRows.length === 0;
  const clearButton = (
    <Button variant="secondary" size="sm" onClick={clearAll}>
      Clear filters
    </Button>
  );

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
      <div className="flex min-w-0 items-center gap-2">
        <h3 className="text-sm font-medium text-fg">Counsellors</h3>
        <span className="text-xs text-fg-muted tabular-nums">
          {activeCount > 0
            ? `${visibleRows.length.toLocaleString('en-GB')} of ${countText(rows.length)}`
            : countText(rows.length)}
        </span>
        {activeCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAll}>
            Clear filters
          </Button>
        )}
      </div>
      {rows.length > 0 && (
        <div className="flex items-center gap-2">
          <FilterSheet
            keys={visibleKeys}
            headerFor={headerFor}
            activeCount={activeCount}
            clearAll={clearAll}
            className="md:hidden"
          />
          {/* Withheld until the stored layout arrives, so a toggle cannot overwrite it. */}
          {!layout.loading && <ColumnManager layout={layout} labelOf={labelOf} lockedKeys={LOCKED_KEYS} />}
        </div>
      )}
    </div>
  );

  if (rows.length === 0) {
    return (
      <section className="min-w-0">
        {toolbar}
        <EmptyState title="No counsellors" />
      </section>
    );
  }

  const columnCount = measuredKeys.length;
  const nameOf = (row: CounsellorRow): ReactNode => (
    <div className="flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-xs text-fg-subtle tabular-nums">{row.__rank ?? '—'}</span>
      <span className="truncate font-medium text-fg">{row.name || '—'}</span>
      {row.is_current_rep === false && (
        <Badge tone="neutral" className="shrink-0">
          Former
        </Badge>
      )}
    </div>
  );

  return (
    <section className="min-w-0">
      {toolbar}

      <TableWrap frozenHeader frozenMaxHeight="min(60vh, 420px)" className="max-md:hidden">
        <Table>
          <THead>
            {/* One cell per measured key, in order: `useFrozenOffsets` indexes by position. */}
            <TR ref={headRowRef}>
              <TH numeric className="w-10" {...frozenProps(RANK_KEY)}>
                #
              </TH>
              {visibleKeys.map((key) => (
                <FilterableTH key={key} {...(headers.get(key) ?? { label: key })} {...frozenProps(key)} />
              ))}
            </TR>
          </THead>
          <TBody>
            {filteredEmpty ? (
              <TR>
                <TD colSpan={columnCount}>
                  <EmptyState title="No rows match these filters" action={clearButton} />
                </TD>
              </TR>
            ) : (
              ranked.map((row) => (
                <TR key={row.representative_id}>
                  <TD numeric {...frozenProps(RANK_KEY)}>
                    {rankColumn?.render(row) ?? row.__rank}
                  </TD>
                  {visibleKeys.map((key) => {
                    const column = columnByKey.get(key);
                    return (
                      <TD key={key} numeric={column?.numeric} className={column?.className} {...frozenProps(key)}>
                        {column?.render(row)}
                      </TD>
                    );
                  })}
                </TR>
              ))
            )}
          </TBody>
          {footer && !filteredEmpty && (
            <tfoot>
              {/* Sticky to the bottom of the scroll area; not frozen sideways, so the footer's
               * own background never races a frozen cell's. */}
              <tr>
                {measuredKeys.map((key) => (
                  <TD
                    key={key}
                    numeric={key !== NAME_KEY}
                    className="sticky bottom-0 z-[15] bg-surface-sunken font-medium shadow-[inset_0_1px_0_0_var(--hairline)]"
                  >
                    {footer[key] ?? null}
                  </TD>
                ))}
              </tr>
            </tfoot>
          )}
        </Table>
      </TableWrap>

      <div className="md:hidden">
        {filteredEmpty ? (
          <EmptyState title="No rows match these filters" action={clearButton} />
        ) : (
          <div className="grid min-w-0 gap-2">
            <PiCardList
              items={ranked}
              getKey={(row) => row.representative_id}
              render={(row) => (
                <div className="grid min-w-0 gap-1.5">
                  {nameOf(row)}
                  {metricKeys.map((key) => {
                    const column = columnByKey.get(key);
                    return column ? <PiCardField key={key} label={column.label} value={column.render(row)} /> : null;
                  })}
                </div>
              )}
            />
            {footer && (
              <div className="grid min-w-0 gap-1.5 rounded-lg border border-border bg-surface-sunken p-3">
                <span className="text-sm font-medium text-fg">Team median</span>
                {metricKeys.map((key) => {
                  const column = columnByKey.get(key);
                  return column ? <PiCardField key={key} label={column.label} value={footer[key] ?? '—'} /> : null;
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
